type QtyComboItem = HTMLElement & {
  closeCustom?: () => void;
};

const qtyCombo = {
  combos: [] as QtyComboItem[],
  isBound: false,
  isResetBound: false,
  customValue: 'custom',
  defaultMin: 1,
  defaultMax: 9999,

  init() {
    this.refreshCombos();

    if (!this.combos.length) return;

    this.setCombos();
    this.setOutsideClose();
    this.setResetQty();
  },

  refreshCombos() {
    this.combos = Array.from(
      document.querySelectorAll<QtyComboItem>('[data-qty-select-combo]')
    );
  },

  setResetQty() {
    if (this.isResetBound) return;

    this.isResetBound = true;

    window.addEventListener('resetQty', () => {
      /*
       * Wait until Maropost has finished replacing the template markup.
       */
      requestAnimationFrame(() => {
        this.refreshCombos();
        this.setCombos();
      });
    });
  },

  setCombos() {
    this.combos.forEach((combo) => {
      /*
       * Existing elements keep their listeners.
       * Newly rendered Maropost elements will not have data-qty-init.
       */
      if (combo.dataset.qtyInit === '1') return;

      const form = combo.closest<HTMLFormElement>('form');

      const select = combo.querySelector<HTMLSelectElement>(
        '.qty-select-combo__select'
      );

      const input = combo.querySelector<HTMLInputElement>(
        '.qty-select-combo__input'
      );

      const qtyField = form?.querySelector<HTMLInputElement>('.js-neto-qty');

      if (!form || !select || !input || !qtyField) return;

      combo.dataset.qtyInit = '1';

      const min = this.toInt(qtyField.min) ?? this.defaultMin;
      const max = this.toInt(qtyField.max) ?? this.defaultMax;

      const clamp = (value: number) =>
        Math.min(Math.max(value, min), max);

      const hasOption = (value: string) =>
        Array.from(select.options).some(
          (option) => option.value === value
        );

      const customOption = () =>
        Array.from(select.options).find(
          (option) => option.value === this.customValue
        );

      const setCustomLabel = (label = 'Custom amount') => {
        const option = customOption();

        if (option) {
          option.textContent = label;
        }
      };

      const setQty = (value: number) => {
        const qty = clamp(value);

        qtyField.value = String(qty);

        qtyField.dispatchEvent(
          new Event('input', {
            bubbles: true,
          })
        );

        qtyField.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        return qty;
      };

      const getQty = () =>
        clamp(this.toInt(qtyField.value) ?? min);

      const syncSelect = () => {
        const qty = String(getQty());

        if (hasOption(qty)) {
          select.value = qty;
          setCustomLabel();
          return;
        }

        if (hasOption(this.customValue)) {
          select.value = this.customValue;
          setCustomLabel(qty);
        }
      };

      const showCustom = () => {
        combo.classList.add('is-custom', 'no-chev');

        input.hidden = false;
        input.disabled = false;
        input.min = String(min);
        input.max = String(max);
        input.value = String(getQty());
      };

      const hideCustom = () => {
        combo.classList.remove('is-custom', 'no-chev');

        input.hidden = true;
        input.disabled = true;
      };

      const closeCustom = () => {
        hideCustom();
        syncSelect();
      };

      const setCustomQty = (closeIfPreset = false) => {
        const typedQty = this.toInt(input.value);

        if (typedQty == null) {
          closeCustom();
          return;
        }

        const qty = setQty(typedQty);
        const value = String(qty);

        if (hasOption(value)) {
          select.value = value;
          setCustomLabel();

          if (closeIfPreset) {
            hideCustom();
          }

          return;
        }

        if (hasOption(this.customValue)) {
          select.value = this.customValue;
          input.value = value;
          setCustomLabel(value);
          return;
        }

        hideCustom();
      };

      qtyField.style.display = 'none';

      /*
       * Synchronise the visible combo with the current Neto quantity.
       */
      syncSelect();
      hideCustom();

      select.addEventListener('change', () => {
        if (select.value === this.customValue) {
          showCustom();
          return;
        }

        const qty = this.toInt(select.value);

        if (qty == null) return;

        setQty(qty);
        hideCustom();
        setCustomLabel();
      });

      input.addEventListener('input', () => {
        setCustomQty();
      });

      input.addEventListener('blur', () => {
        setCustomQty(true);
      });

      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        input.blur();
      });

      combo.closeCustom = closeCustom;
    });
  },

  setOutsideClose() {
    if (this.isBound) return;

    this.isBound = true;

    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target as Node | null;

        if (!target) return;

        /*
         * Query the current elements because Maropost may have replaced
         * the original template markup.
         */
        const currentCombos = document.querySelectorAll<QtyComboItem>(
          '[data-qty-select-combo]'
        );

        currentCombos.forEach((combo) => {
          if (!combo.contains(target)) {
            combo.closeCustom?.();
          }
        });
      },
      {
        capture: true,
      }
    );
  },

  toInt(value: string | null | undefined) {
    if (!value?.trim()) return null;

    const number = parseInt(value, 10);

    return Number.isNaN(number) ? null : number;
  },
};

export default qtyCombo;